/**
 * Sol scroll-reveal system.
 *
 * Reveals [data-sol-reveal] elements as they enter the viewport using a
 * single shared IntersectionObserver. Stagger groups ([data-sol-reveal-group])
 * assign an incremental transition delay to their [data-sol-reveal] children.
 *
 * Activation is gated by the .sol-anim class on <html>, which is set
 * synchronously in the document head (see snippets/sol-theme-variables.liquid)
 * only when the merchant has enabled animations and the visitor has no
 * reduced-motion preference. Hidden/visible states live in sol-theme.css.
 *
 * Theme editor: sections are re-scanned on shopify:section:load so reveals
 * keep working when sections are added, reordered or re-rendered.
 */
(function () {
  'use strict';

  if (window.SolReveal) return;

  var observer = null;

  function isEnabled() {
    return document.documentElement.classList.contains('sol-anim');
  }

  function staggerStep() {
    var raw = getComputedStyle(document.documentElement).getPropertyValue('--sol-reveal-stagger');
    var value = parseInt(raw, 10);
    return isNaN(value) ? 90 : value;
  }

  function getObserver() {
    if (observer) return observer;
    observer = new IntersectionObserver(
      function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (!entries[i].isIntersecting) continue;
          entries[i].target.classList.add('sol-revealed');
          observer.unobserve(entries[i].target);
        }
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.06 }
    );
    return observer;
  }

  /**
   * Finds and observes reveal targets inside `root` (defaults to document).
   * Safe to call repeatedly; already-observed or revealed elements are skipped.
   */
  function scan(root) {
    if (!isEnabled() || !('IntersectionObserver' in window)) return;
    var scope = root && root.querySelectorAll ? root : document;

    var groups = scope.querySelectorAll('[data-sol-reveal-group]');
    for (var g = 0; g < groups.length; g++) {
      var children = groups[g].querySelectorAll('[data-sol-reveal]:not(.sol-revealed)');
      var step = staggerStep();
      for (var c = 0; c < children.length; c++) {
        children[c].style.setProperty('--sol-reveal-delay', c * step + 'ms');
      }
    }

    var targets = scope.querySelectorAll('[data-sol-reveal]:not(.sol-revealed):not([data-sol-observed])');
    for (var t = 0; t < targets.length; t++) {
      targets[t].setAttribute('data-sol-observed', '');
      getObserver().observe(targets[t]);
    }
  }

  /** Immediately reveals everything inside `root` (used when animations are off). */
  function revealAll(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var targets = scope.querySelectorAll('[data-sol-reveal]');
    for (var i = 0; i < targets.length; i++) {
      targets[i].classList.add('sol-revealed');
    }
  }

  window.SolReveal = { scan: scan, revealAll: revealAll };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      scan(document);
    });
  } else {
    scan(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    scan(event.target);
  });
})();
